import { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useDispatch } from 'react-redux';
import { removeAllNotification } from 'store/appSlice';
import { initNewVideos } from 'store/videoSlice';
import { v4 as uuid } from 'uuid';

interface IProps {
  menus: any[];
  children: JSX.Element;
  align?: string;
  width?: any;
  position: string;
  dropdownHeader?: any;
  additionalClasses?: string;
  MenuItem: any;
  notificationMenu?: boolean;
  top?: string;
  selector?: string;
  preventClose?: boolean;
  caretIcon?: any;
  fixedHeight?: boolean;
  isFixPosition?: boolean;
}

const scrollContainer = {
  maxHeight: '450px'
};

export const MenuWrapper = ({
  header,
  children,
  align = 'left',
  width,
  position,
  dropdownHeader,
  notificationMenu,
  additionalClasses,
  top,
  selector,
  isExpanded,
  setExpanded,
  preventClose,
  caretIcon,
  fixedHeight,
  isFixPosition
}: {
  header: JSX.Element;
  align?: string;
  children: JSX.Element;
  width?: number;
  position: string;
  dropdownHeader?: any;
  notificationMenu?: boolean;
  additionalClasses?: string;
  top?: string;
  selector?: string;
  isExpanded: boolean;
  setExpanded: any;
  preventClose?: boolean;
  caretIcon?: any;
  fixedHeight?: boolean;
  isFixPosition?: boolean;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLSpanElement>();
  const dispatch = useDispatch();
  // Close menu if user clicks outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setExpanded(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setExpanded]);

  useEffect(() => {
    if (isExpanded) {
      if (selector) {
        const stickyDom = document.getElementById(selector);
        if (stickyDom) {
          stickyDom.style.zIndex = '100';
        }
      }
    } else {
      if (selector) {
        const stickyDom = document.getElementById(selector);
        if (stickyDom) {
          stickyDom.style.zIndex = '40';
        }
      }
    }
  }, [isExpanded]);

  return (
    <>
      <div className={classNames(`relative inline-block text-left`, additionalClasses)}>
        <div
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(true);
          }}
          className={`flex ${isFixPosition ? 'fixed' : ''} ${fixedHeight ? 'w-full' : ''}`}
        >
          {header}
          {caretIcon ? (
            <div
              className={`absolute right-2 top-1/2 -translate-y-1/2 ${isExpanded ? '-rotate-180' : 'rotate-0'}`}
              onClick={() => setExpanded(!isExpanded)}
            >
              {caretIcon()}
            </div>
          ) : (
            <></>
          )}
        </div>
        <div
          className={classNames(
            `origin-top-left mt-2 py-4 rounded-md shadow-pop bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50 ${
              fixedHeight ? 'max-h-[340px] overflow-auto' : ''
            }`,
            position,
            top,
            { 'left-5': align === 'left' },
            { '-right-8': align === 'right' },
            { 'left-1/2 -translate-x-1/2': align === 'center' }
          )}
          ref={wrapperRef}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="menu-button"
          tabIndex={-1}
          style={{ display: isExpanded ? 'block' : 'none', width: width ? width : 200 }}
          onClick={() => {
            if (!preventClose) setExpanded(false);
          }}
        >
          {dropdownHeader && dropdownHeader}
          {notificationMenu ? (
            <PerfectScrollbar
              style={scrollContainer}
              options={{ suppressScrollY: false, suppressScrollX: true }}
              containerRef={(el) => (containerRef.current = el)}
              className="header-dropdown-container"
            >
              {children}
            </PerfectScrollbar>
          ) : (
            children
          )}
        </div>
      </div>
      {isExpanded && (
        <div
          className="fixed w-screen h-screen inset-0 transition-opacity m-0 p-0"
          id="menu-close"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(false);
            if (notificationMenu) {
              dispatch(removeAllNotification());
              dispatch(initNewVideos('home'));
            }
          }}
        />
      )}
    </>
  );
};

export const Menu = ({
  menus,
  children,
  align,
  width,
  position,
  MenuItem,
  additionalClasses,
  dropdownHeader,
  notificationMenu,
  selector,
  top,
  caretIcon,
  fixedHeight,
  isFixPosition
}: IProps) => {
  const [isExpanded, setExpanded] = useState(false);

  return (
    <MenuWrapper
      header={children}
      align={align}
      width={width}
      position={position}
      additionalClasses={additionalClasses}
      dropdownHeader={dropdownHeader}
      notificationMenu={notificationMenu}
      top={top}
      selector={selector}
      isExpanded={isExpanded}
      setExpanded={setExpanded}
      caretIcon={caretIcon}
      fixedHeight={fixedHeight}
      isFixPosition={isFixPosition}
    >
      <div className="relative">
        {menus.map((el: any) => (
          <MenuItem key={uuid()} {...el} width={width} setExpanded={setExpanded} />
        ))}
      </div>
    </MenuWrapper>
  );
};
